import Link from 'next/link';
import { ContactCta } from '@/features/contact-module/ContactCta';
import { resolveHref } from './navigation.utils';
import type { CtaItemDto, NavItemDto } from '@/lib/sanity/dto';
import type { CSSProperties, ReactNode } from 'react';

type BaseLinkProps = {
    item: NavItemDto | CtaItemDto | null;
    className?: string;
    /** Inline custom properties (e.g. a per-item accent colour) — not a styling escape hatch. */
    style?: CSSProperties;
    children?: ReactNode;
    ariaCurrent?: 'page' | 'true';
    scroll?: boolean;
    ariaHaspopup?: 'dialog';
    queryParams?: Record<string, string>;
    onClick?: () => void;
    /** Pointer/focus entry — used where a list highlights the item under the cursor. */
    onMouseEnter?: () => void;
    onFocus?: () => void;
    /** The matching exits, so whatever entry started can be wound back. */
    onMouseLeave?: () => void;
    onBlur?: () => void;
};

export function BaseLink({
    item,
    className,
    style,
    children,
    ariaCurrent,
    scroll,
    ariaHaspopup,
    queryParams,
    onClick,
    onMouseEnter,
    onFocus,
    onMouseLeave,
    onBlur,
}: BaseLinkProps) {
    if (!item) return null;

    const href = resolveHref(item, queryParams);

    // Not a destination: the contact forms open where the item stands. Handled
    // here rather than in CtaButton so every place that turns a nav item into a
    // link gets it — and so the item keeps the caller's own styling either way.
    if (item.linkType === 'contact') {
        return (
            <ContactCta label={item.label ?? ''} fallbackHref={href} className={className}>
                {children ?? item.label}
            </ContactCta>
        );
    }

    if (item.linkType === 'external') {
        return (
            <a
                href={href}
                className={className}
                style={style}
                target="_blank"
                rel="noopener noreferrer"
                aria-current={ariaCurrent}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onFocus={onFocus}
                onMouseLeave={onMouseLeave}
                onBlur={onBlur}
            >
                {children ?? item.label}
            </a>
        );
    }

    return (
        <Link
            href={href}
            className={className}
            style={style}
            aria-current={ariaCurrent}
            aria-haspopup={ariaHaspopup}
            scroll={scroll}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onFocus={onFocus}
            onMouseLeave={onMouseLeave}
            onBlur={onBlur}
        >
            {children ?? item.label}
        </Link>
    );
}
